import React, { ReactNode, useEffect } from 'react';
import { UserProvider, useUser } from './UserProvider';
import { ThemeProvider } from './ThemeProvider';
import { NotificationProvider, useNotifications, NotificationType } from './NotificationProvider';
import { UIProvider, MobileTopNav } from './UIProvider';
import { ProductProvider, Product } from './ProductProvider';
import { ChatMessageProvider } from './ChatMessageProvider';
import { ListingProvider } from './ListingProvider';
import { SearchProvider } from './SearchProvider';
import { AppSessionProvider, NotificationPayload, UserProfile as SessionUserProfile } from './AppSessionProvider';
import { UnifiedAuthProvider } from './UnifiedAuthProvider';
import { CategoryProvider } from './CategoryProvider';
import { PartnersDataProvider } from './PartnersDataProvider';
import { PartnerProvider } from '../utils/partner';
import { RouterProvider } from './RouterProvider';
import { UiTestProvider } from './UiTestProvider';
import {
  ProviderErrorBoundary,
  AsyncErrorBoundary,
} from '../components/error-boundaries';
import BidstreamTag from '../components/embed-tags/bidstream';
import { SellwildVideoAdsTag } from '../components/embed-tags/sellwild-video-ads';
import { SellwildDisplayAdsTag } from '../components/embed-tags/sellwild-display-ads';

/**
 * Helper component to show logout notification if flag is set
 */
const LogoutNotificationHelper: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { createNotification } = useNotifications();

  useEffect(() => {
    // Check for logout notification flag - run only once on mount (effects are browser-only)
    const showLogoutNotification = localStorage.getItem('showLogoutNotification');
    if (showLogoutNotification === 'true') {
      // Check timestamp to prevent showing multiple times
      const lastLogoutNotification = localStorage.getItem('lastLogoutNotification');
      const now = Date.now();
      const shouldShow = !lastLogoutNotification || (now - parseInt(lastLogoutNotification)) > 10000;

      if (shouldShow) {
        // Show notification
        createNotification({
          msg: 'You have been logged out',
          type: 'success',
          dismissed: false,
        });
        // Clear the flag and set timestamp
        localStorage.removeItem('showLogoutNotification');
        localStorage.setItem('lastLogoutNotification', now.toString());
      } else {
        // Just clear the flag without showing notification
        localStorage.removeItem('showLogoutNotification');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount

  return children as React.ReactElement;
};

/**
 * Helper component to bridge AppSessionProvider notifications to NotificationProvider
 */
const SessionNotificationBridge: React.FC<{
  children: (onNotification: (notification: NotificationPayload) => void) => ReactNode;
}> = ({ children }) => {
  const { createNotification } = useNotifications();

  const handleNotification = (notification: NotificationPayload) => {
    createNotification({
      type: notification.type as NotificationType,
      msg: notification.msg,
      dismissed: false,
    });
  };

  return <>{children(handleNotification)}</>;
};

interface CompositeAppProviderProps {
  children: ReactNode;
  // Initial values from server-side props
  currentCatId?: number;
  currentList?: Product[];
  recommendedList?: Product[];
  otherList?: Product[];
  featuredAisles?: unknown[];
  productDetail?: Product;
  mobileTopNav?: MobileTopNav;
  loginOpen?: boolean;
  // Category data from SSR (legacy props, kept for backwards compatibility)
  initialCategories?: { id: number; label: string; slug: string }[];
  initialCategoryGroups?: { id: number; label: string; slug: string }[];
  initialVisibleCategories?: number[];
}

/**
 * Wrapper component that accesses UserProvider context
 * and passes profile to AppSessionProvider
 */
const AppSessionProviderWrapper: React.FC<{
  children: ReactNode;
  onNotification?: (notification: NotificationPayload) => void;
}> = ({ children, onNotification }) => {
  const { profile } = useUser();
  const { fetchUnreadCounts } = useNotifications();

  return (
    <AppSessionProvider
      profile={profile as unknown as SessionUserProfile}
      onNotification={onNotification}
      onTransactionRead={fetchUnreadCounts}
    >
      {children}
    </AppSessionProvider>
  );
};


/**
 * CompositeAppProvider combines all individual context providers
 * into a single provider hierarchy, maintaining the proper order
 * of dependencies between providers.
 */
export const CompositeAppProvider: React.FC<CompositeAppProviderProps> = (props) => {
  const {
    children,
    currentCatId,
    currentList,
    recommendedList,
    otherList,
    featuredAisles,
    productDetail,
    mobileTopNav = 'left' as MobileTopNav,
    loginOpen = false,
    initialCategories,
    initialCategoryGroups,
    initialVisibleCategories,
  } = props;
  return (
    <PartnerProvider>
      <RouterProvider>
        {/* Above UIProvider: the home page's starting view is a UI test (utils/ui-tests/registry.ts). */}
        <UiTestProvider>
        <ThemeProvider>
          <ProviderErrorBoundary providerName="User" allowPartialFailure={false}>
            <UserProvider>
            {/* Temporarily removing error boundary around Auth to fix OAuth */}
            <UnifiedAuthProvider>
              <NotificationProvider>
                <LogoutNotificationHelper>
                  <SessionNotificationBridge>
                    {(handleNotification) => (
                      <ProviderErrorBoundary providerName="AppSession" allowPartialFailure={true}>
                        <AppSessionProviderWrapper
                          onNotification={handleNotification}
                        >
                          <UIProvider
                            initialLoginOpen={loginOpen}
                            initialMobileTopNav={mobileTopNav}
                          >
                            <CategoryProvider
                              initialCategories={initialCategories}
                              initialCategoryGroups={initialCategoryGroups}
                              initialVisibleCategories={initialVisibleCategories}
                            >
                              <PartnersDataProvider>
                                <AsyncErrorBoundary>
                                  <ProductProvider
                                    initialProductDetail={productDetail}
                                    initialCurrentList={currentList}
                                    initialRecommendedList={recommendedList}
                                    initialOtherList={otherList}
                                  >
                                    <SearchProvider initialCategoryGroupId={currentCatId}>
                                      <ListingProvider featuredAisles={featuredAisles}>
                                        <ChatMessageProvider>
                                          {children}
                                          <BidstreamTag />
                                          <SellwildVideoAdsTag />
                                          <SellwildDisplayAdsTag />
                                        </ChatMessageProvider>
                                      </ListingProvider>
                                    </SearchProvider>
                                  </ProductProvider>
                                </AsyncErrorBoundary>
                              </PartnersDataProvider>
                            </CategoryProvider>
                          </UIProvider>
                        </AppSessionProviderWrapper>
                      </ProviderErrorBoundary>
                    )}
                  </SessionNotificationBridge>
                </LogoutNotificationHelper>
              </NotificationProvider>
            </UnifiedAuthProvider>
            </UserProvider>
          </ProviderErrorBoundary>
        </ThemeProvider>
        </UiTestProvider>
      </RouterProvider>
    </PartnerProvider>
  );
};

export default CompositeAppProvider;